import type { Editor } from "@tiptap/core"
import styles from "./LinkHoverMenu.module.css"

import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { cx } from "@shared/components/untitledui/util/cx"
import { attachmentIdOfNode } from "@shared/unified-tiptap/analytics/node-ids"
import { OPERATION_CONTRACTS } from "@shared/unified-tiptap/analytics/operations"
import {
  isLinkNode,
  type LinkDialogOrigin,
} from "@shared/unified-tiptap/extensions/link/link-attrs"
import type { SurfaceArbiter } from "@shared/unified-tiptap/surface-arbiter"
import { effect } from "@supergrain/kernel"
import { Plugin, PluginKey, type PluginView } from "@tiptap/pm/state"
import type { EditorView } from "@tiptap/pm/view"
import { Globe01, Trash01 } from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"
import { useHover } from "react-aria"
import { Toolbar } from "react-aria-components"

const pluginKeyName = "linkHoverMenu"

/**
 * Wait time between when a link or its menu stop being hovered and when the menu closes.
 *
 * This must be long enough that the user can move their cursor from the link to the menu.
 */
const HOVER_CLOSE_TIMEOUT_MS = 300

/**
 * The shared hover menu for unified link tokens, in every form: URL chip + delete +
 * edit, per the attachment menu (docs/unified-links.md §6). A floating menu based on
 * the @tiptap/react BubbleMenu.
 */
export function LinkHoverMenu({
  editor,
  openLinkDialog,
  arbiter,
}: {
  editor: Editor
  openLinkDialog: (origin: LinkDialogOrigin) => void
  arbiter: SurfaceArbiter
}) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isHoveringMenuRef = useRef<((isHovering: boolean) => void) | null>(null)
  const currentTargetRef = useRef<HTMLAnchorElement | null>(null)
  const forceHideRef = useRef<(() => void) | null>(null)

  // The hovered token's URL, for the chip; the plugin pushes it on every target change.
  const [url, setUrl] = useState("")
  const onTargetChangeRef = useRef<((url: string) => void) | null>(null)
  onTargetChangeRef.current = setUrl

  useEffect(() => {
    const plugin = LinkHoverMenuPlugin(
      editor,
      wrapperRef.current!,
      isHoveringMenuRef,
      currentTargetRef,
      onTargetChangeRef,
      forceHideRef,
      arbiter,
    )
    editor.registerPlugin(plugin)

    return () => {
      editor.unregisterPlugin(pluginKeyName)
    }
  }, [editor])

  const { hoverProps: menuHoverProps } = useHover({
    onHoverStart: () => isHoveringMenuRef.current?.(true),
    onHoverEnd: () => isHoveringMenuRef.current?.(false),
  })

  return (
    <div
      ref={wrapperRef}
      className={cx(
        styles.linkHoverMenuWrapper,
        "rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt",
      )}
      data-is-hidden=""
      {...menuHoverProps}
    >
      {/* react-aria Toolbar: role="toolbar" + arrow-key roving across the chip and
          buttons come from the component — no hand-written role or focus handling. */}
      <Toolbar aria-label="Link actions" className="flex flex-row items-center gap-1">
        {/* URL chip (globe + address), opens the link — the attachment menu's pattern. */}
        <a
          href={url}
          title={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex max-w-64 items-center gap-1 px-1.5 text-sm text-secondary hover:underline"
        >
          <Globe01 className="size-4 shrink-0" />
          <span className="truncate">{url}</span>
        </a>
        <Button
          color="tertiary"
          size="xs"
          iconLeading={Trash01}
          aria-label="Delete link"
          onPress={() => {
            const pos = findTokenPos(editor, currentTargetRef.current!)
            if (pos === null) return
            const token = editor.state.doc.nodeAt(pos)
            const attachmentId = token && attachmentIdOfNode(token)

            // Hide the menu immediately so it doesn't teleport while waiting for the hideTimeout.
            forceHideRef.current?.()

            const chain = editor.chain()
            if (attachmentId) {
              chain.label(OPERATION_CONTRACTS.OP_REMOVE_LINK, "link-popover", { attachmentId })
            }
            chain.setNodeSelection(pos).deleteSelection().focus().run()
          }}
        />
        <Button
          color="tertiary"
          size="xs"
          onPress={() => {
            const pos = findTokenPos(editor, currentTargetRef.current!)
            if (pos === null) return

            // Select the token so the EditLink dialog applies to it.
            editor.commands.setNodeSelection(pos)

            // Hide the menu immediately so it doesn't teleport while waiting for the hideTimeout.
            forceHideRef.current?.()

            openLinkDialog("link-popover")
          }}
        >
          Edit
        </Button>
      </Toolbar>
    </div>
  )
}

/**
 * Returns a plugin that absolutely positions the LinkHoverMenu and shows/hides it, using floating-ui.
 */
function LinkHoverMenuPlugin(
  editor: Editor,
  menuElt: HTMLDivElement,
  isHoveringMenuRef: React.RefObject<((isHovering: boolean) => void) | null>,
  currentTargetRef: React.RefObject<HTMLAnchorElement | null>,
  onTargetChangeRef: React.RefObject<((url: string) => void) | null>,
  forceHideRef: React.RefObject<(() => void) | null>,
  arbiter: SurfaceArbiter,
) {
  return new Plugin({
    key: new PluginKey(pluginKeyName),
    view: (view) =>
      new LinkHoverMenuPluginView(
        editor,
        view,
        menuElt,
        isHoveringMenuRef,
        currentTargetRef,
        onTargetChangeRef,
        forceHideRef,
        arbiter,
      ),
  })
}

class LinkHoverMenuPluginView implements PluginView {
  /**
   * The current target, which has the menu open.
   *
   * It will remain the current target until the target and menu are both not hovered
   * for a sufficient period of time.
   *
   * Non-null precisely when the menu is open.
   */
  private currentTarget: HTMLAnchorElement | null = null

  /** The link the pointer is over right now, which may differ from the open menu's target. */
  private hoveredTarget: HTMLAnchorElement | null = null
  private isHoveringMenu = false
  private hideTimeout: ReturnType<typeof setTimeout> | null = null

  private cleanupAutoUpdate: (() => void) | null = null

  private stopVisibilityEffect: () => void

  constructor(
    readonly editor: Editor,
    readonly view: EditorView,
    readonly menuElt: HTMLDivElement,
    isHoveringMenuRef: React.RefObject<((isHovering: boolean) => void) | null>,
    readonly currentTargetRef: React.RefObject<HTMLAnchorElement | null>,
    readonly onTargetChangeRef: React.RefObject<((url: string) => void) | null>,
    forceHideRef: React.RefObject<(() => void) | null>,
    readonly arbiter: SurfaceArbiter,
  ) {
    isHoveringMenuRef.current = this.setIsHoveringMenu
    forceHideRef.current = this.clearTarget
    this.view.dom.addEventListener("mouseover", this.onMouseOver)
    // mouseover will detect when we move the mouse over something else within the editor,
    // but we also need to detect leaving the editor completely.
    this.view.dom.addEventListener("mouseleave", this.onMouseLeave)
    // Re-apply the arbiter's verdict when it changes for reasons outside this plugin
    // (a selection becoming eligible, an overlay opening, a drag starting). The first
    // line is the tracked signal read; kernel reruns this effect only when it flips.
    this.stopVisibilityEffect = effect(() => {
      void this.arbiter.visible.linkHoverMenu
      this.applyVisibility()
    })
  }

  // ----------
  // Target hover tracking
  // ----------

  private onMouseOver = (event: MouseEvent) => {
    // Check if the mouse is over a link within the editor.
    if (!(event.target instanceof HTMLElement)) {
      this.setHoveredTarget(null)
      return
    }
    const link = event.target.closest<HTMLAnchorElement>("a[data-is-tiptap-link]")
    if (!link || !this.view.dom.contains(link)) {
      this.setHoveredTarget(null)
      return
    }

    this.setHoveredTarget(link)
  }

  update() {
    this.updateVisibility()
  }

  private onMouseLeave = () => {
    this.setHoveredTarget(null)
  }

  private setHoveredTarget(hoveredTarget: HTMLAnchorElement | null) {
    this.hoveredTarget = hoveredTarget
    // A link crossed on the way from the target to the menu must not drag the menu out from
    // under the pointer: while a menu is open, another link takes over only by outlasting the
    // grace period (see onHideTimeout), never on the mousemove sample that first touches it.
    if (hoveredTarget && this.currentTarget === null) this.setTarget(hoveredTarget)
    this.checkScheduleHide()
  }

  // ----------
  // Menu hover tracking & scheduling hiding
  // ----------

  private setIsHoveringMenu = (isHoveringMenu: boolean) => {
    this.isHoveringMenu = isHoveringMenu
    this.checkScheduleHide()
  }

  /**
   * Schedule or unschedule hiding as needed, according to the hover states.
   *
   * Called whenever this.hoveredTarget or this.isHoveringMenu changes.
   */
  private checkScheduleHide() {
    if (!this.currentTarget) return

    if (this.hoveredTarget === this.currentTarget || this.isHoveringMenu) {
      if (this.hideTimeout !== null) {
        clearTimeout(this.hideTimeout)
        this.hideTimeout = null
      }
      return
    } else {
      if (this.hideTimeout !== null) return

      this.hideTimeout = setTimeout(this.onHideTimeout, HOVER_CLOSE_TIMEOUT_MS)
    }
  }

  /**
   * The grace period expired: hand the menu to whatever link the pointer settled on, or
   * close it if the pointer is elsewhere.
   */
  private onHideTimeout = () => {
    this.hideTimeout = null
    // A hovered link that has since left the document (deleted, re-rendered) is no target.
    if (this.hoveredTarget && this.view.dom.contains(this.hoveredTarget)) {
      this.setTarget(this.hoveredTarget)
    } else this.clearTarget()
  }

  // ----------
  // Show & hide
  // ----------

  /**
   * Shows the menu with the given target, if not already shown.
   *
   * Also handles changing target.
   */
  private setTarget(target: HTMLAnchorElement) {
    if (target === this.currentTarget) return

    this.currentTarget = target
    this.currentTargetRef.current = target
    this.onTargetChangeRef.current?.(target.href)
    this.updateVisibility()
  }

  private show(target: HTMLAnchorElement) {
    this.cleanupAutoUpdate?.()
    this.menuElt.removeAttribute("data-is-hidden")

    const updatePosition = () => {
      void computePosition(target, this.menuElt, {
        placement: "bottom",
        middleware: [offset(6), flip(), shift({ padding: 4 })],
      }).then(({ x, y }) => {
        this.menuElt.style.left = `${x}px`
        this.menuElt.style.top = `${y}px`
      })
    }

    this.cleanupAutoUpdate = autoUpdate(target, this.menuElt, updatePosition)
  }

  /**
   * The hover boundary: report this surface's input (the target, which outlives raw
   * hover by the grace window) and trust the arbiter's verdict unconditionally.
   */
  private updateVisibility() {
    this.arbiter.report({ isLinkHovered: this.currentTarget !== null })
    this.applyVisibility()
  }

  /** Paint the current verdict (called directly on target changes, and by the effect). */
  private applyVisibility() {
    const target = this.currentTarget
    if (target && this.arbiter.visible.linkHoverMenu) this.show(target)
    else this.hideMenuElement()
  }

  private hideMenuElement() {
    this.cleanupAutoUpdate?.()
    this.cleanupAutoUpdate = null
    this.menuElt.setAttribute("data-is-hidden", "")
  }

  private clearTarget = () => {
    if (!this.currentTarget) return
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    this.currentTarget = null
    this.currentTargetRef.current = null
    this.updateVisibility()
  }

  // ----------
  // Misc
  // ----------

  destroy() {
    this.stopVisibilityEffect()
    this.clearTarget()

    this.view.dom.removeEventListener("mouseover", this.onMouseOver)
    this.view.dom.removeEventListener("mouseleave", this.onMouseLeave)
  }
}

/**
 * Finds the document position of the link token rendered as the given DOM anchor,
 * whichever form (linkInline or linkBlock) it is.
 */
function findTokenPos(editor: Editor, link: HTMLAnchorElement): number | null {
  const pos = editor.view.posAtDOM(link, 0)
  if (pos < 0) return null

  const $pos = editor.state.doc.resolve(Math.min(pos, editor.state.doc.content.size))
  if ($pos.nodeAfter && isLinkNode($pos.nodeAfter)) return $pos.pos
  if ($pos.nodeBefore && isLinkNode($pos.nodeBefore)) return $pos.pos - $pos.nodeBefore.nodeSize
  for (let depth = $pos.depth; depth > 0; depth--) {
    if (isLinkNode($pos.node(depth))) return $pos.before(depth)
  }
  return null
}
